import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import OpportunityMultilineField from './OpportunityMultilineField';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RightChatDrawer, { ChatSession } from '../RightChatDrawer';
import { getLatestQueryHistory, deleteIdeationIntake } from '../../IdeationCOI/IdeationService';
//import { formatDate, getFieldTypeLabel } from '../../../common/utils';
import ShowToast, { ToastTypeEnum } from '../../../common/components/ShowToast';
import useOpportunityDetailsForm, {
  OpportunityDetailFieldKey,
} from '../hooks/useOpportunityDetailsForm';
import { RootState } from '../../../store';
import { VENDOR_LIST } from '../../../appConstants';
import CloseIcon from '@mui/icons-material/Close';
import {
  resetFunctions,
  resetProcessGroups,
  resetProcesses,
  setCustomFunctions,
  setProcessGroups,
  setProcesses,
} from '../../IdeationXPLR/ideationFormSlice';
import {
  createUseCase,
  CreateUseCasePayload,
  CreateUseCaseResponse,
  getCustomFunctions,
  getCustomProcesses,
  getProcessGroups,
} from '../../../services/llm_api_services';
import {
  addProcessInOrganization,
  createNewProcess,
  getEnterpriseTechnology,
} from '../../../services/commonService';
import { updateDraftStatus } from '../../IdeationChatbot/IdeationService';
import {
  updateIdeationFieldState,
  clearUseDataXplrField,
  UseDataXplrFieldKey,
  addLinkedSessions,
  clearLinkedSessions,
  // NewIdeationState,
} from '../../../slices/ideationSlice/newIdeationSlice';
import { isContentEmpty } from '../../../common/utils';
import { buildIdQuery } from '../../SimulationXPLR/utils';
import { fieldAPIKeyMap, fieldLabelMap } from './constants';
import EvaluationReportPanel from '../../NewSolutionXPLR/GeneralInformation/EvaluationReportPanel';
import { marked } from 'marked';
export const opportunityDetailsFieldKeyMap = {
  id: 'id',
  currentProcess: 'currentProcess',
  expectedImpact: 'expectedImpact',
  specificIdeasOfImprovement: 'specificIdeasOfImprovement',
  keyPerformanceIndicators: 'keyPerformanceIndicators',
  domain: 'domain',
  selectedFunction: 'selectedFunction',
  selectedFunctionId: 'selectedFunctionId',
  selectedProcessGroup: 'selectedProcessGroup',
  selectedProcessGroupId: 'selectedProcessGroupId',
  selectedProcess: 'selectedProcess',
  selectedProcessId: 'selectedProcessId',
  technologies: 'technologies',
  uploadedFiles: 'uploadedFiles',
  selectedContexts: 'selectedContexts',
  detailsText: 'detailsText',
  taxonomy: 'taxonomy',
  status: 'status',
  finalSummary: 'finalSummary',
  prefillUseCaseId: 'prefillUseCaseId',
  recent_imported_chat: 'recent_imported_chat',
} as Record<OpportunityDetailFieldKey, OpportunityDetailFieldKey>;

interface OpportunityDetailsProps {
  setActiveStep: (step: string) => void;
  setField: Dispatch<SetStateAction<OpportunityDetailFieldKey>>;
  onFormCompletionChange?: (isComplete: boolean) => void;
  handleCloseEvaluate?: () => void;
  evaluateObject?: {
    loading: boolean;
    data: string;
    error: Error | string | null;
    isOpen: boolean;
  };
}

export const DOMAIN_OPTIONS = [
  { label: 'Back Office', value: 'backoffice' },
  { label: 'Middle Office', value: 'middleoffice' },
  { label: 'Front Office', value: 'frontoffice' },
] as const;

const DOMAIN_OFFICE_MAP: Record<string, number> = {
  backoffice: 3,
  middleoffice: 2,
  frontoffice: 1,
};

const USE_DATA_XPLR_TRACKED_FIELDS: UseDataXplrFieldKey[] = [
  opportunityDetailsFieldKeyMap.currentProcess as UseDataXplrFieldKey,
  opportunityDetailsFieldKeyMap.expectedImpact as UseDataXplrFieldKey,
  opportunityDetailsFieldKeyMap.specificIdeasOfImprovement as UseDataXplrFieldKey,
  opportunityDetailsFieldKeyMap.keyPerformanceIndicators as UseDataXplrFieldKey,
] as const;

const isUseDataXplrTrackedField = (
  field: OpportunityDetailFieldKey
): field is UseDataXplrFieldKey => {
  return (USE_DATA_XPLR_TRACKED_FIELDS as readonly string[]).includes(field);
};

type CustomFunctionOption = {
  id: string;
  name: string;
};

export interface GroupedCustomFunction {
  name: string;
  id: string[];
}

type ProcessGroupOption = {
  id: string;
  name: string;
};

type ProcessOption = {
  process_id: string;
  name: string;
};

const isCustomFunctionOption = (value: unknown): value is CustomFunctionOption => {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { id?: unknown }).id === 'string' &&
    typeof (value as { name?: unknown }).name === 'string'
  );
};

const isProcessGroupOption = (value: unknown): value is ProcessGroupOption => {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { id?: unknown }).id === 'string' &&
    typeof (value as { name?: unknown }).name === 'string'
  );
};

const isProcessOption = (value: unknown): value is ProcessOption => {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { process_id?: unknown }).process_id === 'string' &&
    typeof (value as { name?: unknown }).name === 'string'
  );
};

const OpportunityDetails = ({
  // setActiveStep,
  // setField,
  onFormCompletionChange,
  evaluateObject,
  handleCloseEvaluate,
}: OpportunityDetailsProps) => {
  const { formState, updateFieldValue } = useOpportunityDetailsForm();
  const dispatch = useDispatch();
  const {
    customFunctions,
    processGroups,
    processes,
    loadedDomain,
    loadedFunctionId,
    loadedProcessGroupId,
  } = useSelector((state: RootState) => state.ideationForm);

  const { recent_imported_chat, linkedSessions } = useSelector(
    (state: RootState) => state.newIdeation
  );

  const [functionsLoading, setFunctionsLoading] = useState(false);
  const [processGroupsLoading, setProcessGroupsLoading] = useState(false);
  const [processesLoading, setProcessesLoading] = useState(false);
  const [technologiesLoading, setTechnologiesLoading] = useState(false);
  const [technologyOptions, setTechnologyOptions] = useState<string[]>([]);

  const organizationId = useMemo(() => localStorage.getItem('current_organization') ?? '', []);
  const fallbackTechnologies = useMemo(() => VENDOR_LIST.map((vendor) => vendor.label), []);
  const lastSubmittedSnapshotRef = useRef<string | null>(null);
  const isCreatingUseCaseRef = useRef(false);
  const isPatchingUseCaseRef = useRef(false);
  const patchDebounceTimeoutRef = useRef<number | null>(null);
  const [newProcessName, setNewProcessName] = useState('');
  const [createNewProcessModalOpen, setCreateNewProcessModalOpen] = useState(false);
  const [processSelectOpen, setProcessSelectOpen] = useState(false);
  const [groupedFunctions, setGroupedFunctions] = useState<GroupedCustomFunction[]>([]);
  const [groupedProcess, setGroupedProcess] = useState<GroupedCustomFunction[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentFieldLabel, setCurrentFieldLabel] = useState<string>('');
  // const [chatId, setChatId] = useState<string>(initialChatId || '30bb4564-b093-4657-829f-c23c893aeec5');

  const [selectedCategory, setSelectedCategory] = useState<string>('Current Process');
  // const [chatSessions, setChatSessions] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Track if the component has completed initial load to prevent delete API calls during draft loading
  const isInitialLoadRef = useRef(true);
  const initialLoadTimeoutRef = useRef<number | null>(null);

  // Set initial load to false after component mounts and data is loaded
  useEffect(() => {
    // Give time for draft data to be loaded before allowing delete operations
    initialLoadTimeoutRef.current = window.setTimeout(() => {
      isInitialLoadRef.current = false;
    }, 1500); // 1.5 second delay to allow draft loading

    return () => {
      if (initialLoadTimeoutRef.current) {
        window.clearTimeout(initialLoadTimeoutRef.current);
      }
    };
  }, []);

  // const fetchSessions = useCallback(async () => {
  //   try {
  //     const response = await getLatestQueryHistory(50, organizationId);

  //     if (response?.data) {
  //       const formattedSessions = response.data.reduce((acc: any[], item: LatestQueryHistory) => {
  //         if (item?.semantic_knowledge?.report) {
  //           acc.push({
  //             id: item.id,
  //             conversation_id: item.conversation_id,
  //             use_case_title: item.session_name || 'Untitled',
  //             created_by: item.created_by_user_name,
  //             created_at: formatDate(item.created_at),
  //             usedInProcess: item.field_type ? getFieldTypeLabel(item.field_type) : 'N/A',
  //             use_case_id: item?.use_case_id || '',
  //             semantic_knowledge: {
  //               report: item.semantic_knowledge.report,
  //               file_summary: item.semantic_knowledge.file_summary,
  //             },
  //           });
  //         }
  //         return acc;
  //       }, []);

  //       setSessions(formattedSessions);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching sessions:', error);
  //   }
  // }, [organizationId]);

  // useEffect(() => {
  //   if (drawerOpen) {
  //     fetchSessions();
  //   }
  // }, [drawerOpen, fetchSessions]);

  const {
    domain,
    selectedFunction,
    selectedFunctionId,
    selectedProcessGroup,
    selectedProcessGroupId,
    selectedProcess,
    selectedProcessId,
    currentProcess,
    expectedImpact,
    specificIdeasOfImprovement,
    keyPerformanceIndicators,
    technologies,
    id: useCaseId,
  } = formState;

  const isReadOnlyView = false;

  const sanitizeRichTextValue = useCallback((rawValue: string) => {
    if (!rawValue) {
      return '';
    }
    return isContentEmpty(rawValue) ? '' : rawValue;
  }, []);

  // const removeEmptyFields = useCallback(<T extends Record<string, unknown>>(obj: T): Partial<T> => {
  //   const filtered: Partial<T> = {} as Partial<T>;
  //   for (const [key, value] of Object.entries(obj)) {
  //     // Skip null, undefined, empty strings, and empty arrays
  //     if (
  //       value !== null &&
  //       value !== undefined &&
  //       value !== '' &&
  //       !(Array.isArray(value) && value.length === 0)
  //     ) {
  //       (filtered as Record<string, unknown>)[key] = value;
  //     }
  //   }
  //   return filtered;
  // }, []);

  const removeEmptyFields = useCallback(
    <T extends Record<string, unknown>>(
      obj: T,
      options?: { keepEmptyStrings?: boolean; keepEmptyArrays?: boolean }
    ): Partial<T> => {
      const { keepEmptyStrings = false, keepEmptyArrays = false } = options || {};
      const filtered: Partial<T> = {} as Partial<T>;
      for (const [key, value] of Object.entries(obj)) {
        // remove only null/undefined always
        if (value === null || value === undefined) {
          continue;
        }

        // handle empty string
        if (!keepEmptyStrings && value === '') {
          continue;
        }

        // handle empty array
        if (!keepEmptyArrays && Array.isArray(value) && value.length === 0) {
          continue;
        }

        (filtered as Record<string, unknown>)[key] = value;
      }
      return filtered;
    },
    []
  );

  const handleChange = useCallback(
    (field: OpportunityDetailFieldKey, value: string, preserveLinkedSessions = false) => {
      if (isReadOnlyView) return;

      const sanitizedValue = sanitizeRichTextValue(value);
      updateFieldValue(field, sanitizedValue);

      if (
        isUseDataXplrTrackedField(field) &&
        !preserveLinkedSessions &&
        !isInitialLoadRef.current
      ) {
        if (linkedSessions[field]?.length > 0) {
          // UI clear - remove the chip
          dispatch(clearUseDataXplrField(field));
          dispatch(clearLinkedSessions({ field }));

          const fieldType = fieldAPIKeyMap[field];
          if (useCaseId && fieldType) {
            void deleteIdeationIntake(useCaseId, fieldType);
          }
        }
      }
    },
    [sanitizeRichTextValue, updateFieldValue, isReadOnlyView, dispatch, linkedSessions, useCaseId]
  );

  const resetDependentSelections = useCallback(() => {
    updateFieldValue('selectedFunction', '');
    updateFieldValue('selectedFunctionId', '');
    updateFieldValue('selectedProcessGroup', '');
    updateFieldValue('selectedProcessGroupId', '');
    updateFieldValue('selectedProcess', '');
    updateFieldValue('selectedProcessId', '');
  }, [updateFieldValue]);

  const handleDomainChange = useCallback(
    (value: string) => {
      if (isReadOnlyView || domain === value) {
        return;
      }

      updateFieldValue('domain', value);
      resetDependentSelections();
      dispatch(resetFunctions());
    },
    [domain, resetDependentSelections, updateFieldValue, dispatch, isReadOnlyView]
  );

  const dedupeByName = useCallback(<T extends { name: string }>(items: T[]): T[] => {
    const seen = new Set<string>();
    return items.filter((item) => {
      if (seen.has(item.name)) {
        return false;
      }
      seen.add(item.name);
      return true;
    });
  }, []);

  const getFieldTypeFromCategory = (category: string): string | undefined => {
    const categoryToFieldMap: Record<string, string> = {
      'Current Process': fieldAPIKeyMap.currentProcess,
      'Expected Impact': fieldAPIKeyMap.expectedImpact,
      KPIs: fieldAPIKeyMap.keyPerformanceIndicators,
      'Specific Idea of Improvement': fieldAPIKeyMap.specificIdeasOfImprovement,
    };
    return categoryToFieldMap[category];
  };

  // Handlers for category and search changes
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  const handleSearchChange = (searchQuery: string) => {
    setSearchQuery(searchQuery);
  };

  useEffect(() => {
    // if (chatId) {
    loadSessions(getFieldTypeFromCategory(selectedCategory));
    // }
  }, [selectedCategory, searchQuery]);

  const loadSessions = async (fieldType?: string) => {
    try {
      const sessions = await getLatestQueryHistory(20, organizationId, fieldType);
      if (sessions?.data) {
        let sessionList = (sessions?.data || []).map((item: any) => ({
          id: item.id,
          use_case_title:
            item.session_name || item.ideation_intake[0]?.ideation_name || 'Untitled Session',
          session_name: item.session_name,
          created_by: item.created_by_user_name || 'admin',
          updated_by: item.updated_by_user_name || '',
          created_at: item.created_at,
          usedInProcess: item.field_type
            ? fieldLabelMap
              ? fieldLabelMap[item.field_type] || item.field_type
              : item.field_type
            : 'Unknown',
          conversation_id: item.conversation_id,
          version: item.version,
          ideation_intake: item.ideation_intake || [],
          field_type: item.field_type,
          semantic_knowledge: item.semantic_knowledge,
        }));

        // Apply client-side search filtering
        if (searchQuery.trim()) {
          sessionList = sessionList.filter(
            (session) =>
              session.use_case_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (session.session_name &&
                session.session_name.toLowerCase().includes(searchQuery.toLowerCase()))
          );
        }

        setSessions(sessionList);
      }
    } catch (error) {
      console.error('Error loading chat sessions:', error);
    }
  };

  const groupFunctionsByName = useCallback(<T extends { id: string; name: string }>(items: T[]) => {
    const map = new Map<string, string[]>();

    items.forEach((item) => {
      if (!map.has(item.name)) {
        map.set(item.name, [item.id]);
      } else {
        map.get(item.name)!.push(item.id);
      }
    });

    return Array.from(map.entries()).map(([name, id]) => ({
      name,
      id,
    }));
  }, []);

  const loadFunctions = useCallback(async () => {
    if (!organizationId) {
      return;
    }

    if (!formState.domain || !(formState.domain in DOMAIN_OFFICE_MAP)) {
      return;
    }

    if (loadedDomain === formState.domain && customFunctions.length > 0) {
      return;
    }

    const officeId = DOMAIN_OFFICE_MAP[formState.domain];
    if (!officeId) {
      return;
    }

    try {
      setFunctionsLoading(true);
      const response = await getCustomFunctions(organizationId, officeId);
      if (Array.isArray(response)) {
        const normalizedFunctions = response
          .filter(isCustomFunctionOption)
          .map((func: CustomFunctionOption) => ({ id: func.id, name: func.name }));
        const uniqueFunctions = dedupeByName(normalizedFunctions);
        const groupedFunctions = groupFunctionsByName(normalizedFunctions);
        setGroupedFunctions(groupedFunctions);
        dispatch(
          setCustomFunctions({
            functions: uniqueFunctions,
            domain: formState.domain,
          })
        );
      } else {
        dispatch(
          setCustomFunctions({
            functions: [],
            domain: formState.domain,
          })
        );
      }
    } catch (error) {
      console.error('Error loading functions for domain', formState.domain, error);
      dispatch(
        setCustomFunctions({
          functions: [],
          domain: formState.domain,
        })
      );
    } finally {
      setFunctionsLoading(false);
    }
  }, [
    customFunctions.length,
    dedupeByName,
    formState.domain,
    loadedDomain,
    organizationId,
    dispatch,
  ]);

  const loadProcessGroups = useCallback(async () => {
    if (!organizationId || !formState.selectedFunctionId) {
      return;
    }

    if (loadedFunctionId === formState.selectedFunctionId && processGroups.length > 0) {
      return;
    }

    try {
      setProcessGroupsLoading(true);
      const selectedGroupFunction = groupedFunctions.find((func) => func.name === selectedFunction);
      const functionIds = buildIdQuery(selectedGroupFunction?.id ?? []);
      const response = await getProcessGroups(organizationId, functionIds);

      let groups: ProcessGroupOption[] = [];

      if (Array.isArray(response)) {
        groups = response.filter(isProcessGroupOption).map((group: ProcessGroupOption) => ({
          id: group.id,
          name: group.name,
        }));
      } else if (response?.process_groups && Array.isArray(response.process_groups)) {
        groups = response.process_groups
          .filter(isProcessGroupOption)
          .map((group: ProcessGroupOption) => ({
            id: group.id,
            name: group.name,
          }));
      }

      const groupedProcess = groupFunctionsByName(groups);
      setGroupedProcess(groupedProcess);
      dispatch(
        setProcessGroups({
          processGroups: groups,
          functionId: formState.selectedFunctionId,
        })
      );
    } catch (error) {
      console.error('Error loading process groups', error);
      dispatch(
        setProcessGroups({
          processGroups: [],
          functionId: formState.selectedFunctionId,
        })
      );
    } finally {
      setProcessGroupsLoading(false);
    }
  }, [
    dispatch,
    formState.selectedFunctionId,
    loadedFunctionId,
    organizationId,
    processGroups.length,
  ]);

  const loadProcesses = useCallback(async () => {
    if (!formState.selectedProcessGroupId) {
      return;
    }

    if (loadedProcessGroupId === formState.selectedProcessGroupId && processes.length > 0) {
      return;
    }

    try {
      setProcessesLoading(true);
      const selectedProcessGroup = groupedProcess.find(
        (func) => func.name === formState.selectedProcessGroup
      );
      const processGroupIds = buildIdQuery(selectedProcessGroup?.id ?? []);
      const response = await getCustomProcesses(processGroupIds);
      let availableProcesses: ProcessOption[] = [];

      if (response?.processes && Array.isArray(response.processes)) {
        availableProcesses = response.processes
          .filter(isProcessOption)
          .map((proc: ProcessOption) => ({
            process_id: proc.process_id,
            name: proc.name,
          }));
      } else if (Array.isArray(response)) {
        availableProcesses = response.filter(isProcessOption).map((proc: ProcessOption) => ({
          process_id: proc.process_id,
          name: proc.name,
        }));
      }

      dispatch(
        setProcesses({
          processes: availableProcesses,
          processGroupId: formState.selectedProcessGroupId,
        })
      );
    } catch (error) {
      console.error('Error loading processes', error);
      dispatch(
        setProcesses({
          processes: [],
          processGroupId: formState.selectedProcessGroupId,
        })
      );
    } finally {
      setProcessesLoading(false);
    }
  }, [dispatch, formState.selectedProcessGroupId, loadedProcessGroupId, processes.length]);

  const fetchEnterpriseTechnologies = useCallback(async () => {
    if (!organizationId) {
      setTechnologyOptions(fallbackTechnologies);
      return;
    }

    try {
      setTechnologiesLoading(true);
      const response = await getEnterpriseTechnology(organizationId);
      let dropdownTechnologies: string[] = [];

      if (response && response.industry_data && Array.isArray(response.industry_data.functions)) {
        response.industry_data.functions.forEach(
          (func: { technologies?: Array<{ type?: string; name?: string }> }) => {
            func.technologies?.forEach((tech) => {
              if ((tech.type === 'Core' || tech.type === 'Other') && tech.name) {
                dropdownTechnologies.push(tech.name);
              }
            });
          }
        );
      } else if (response && Array.isArray(response.industries)) {
        response.industries.forEach(
          (industry: {
            functions?: Array<{ technologies?: Array<{ type?: string; name?: string }> }>;
          }) => {
            industry.functions?.forEach((func) => {
              func.technologies?.forEach((tech) => {
                if ((tech.type === 'Core' || tech.type === 'Other') && tech.name) {
                  dropdownTechnologies.push(tech.name);
                }
              });
            });
          }
        );
      }

      const uniqueTechnologies = [...new Set(dropdownTechnologies)].sort();
      setTechnologyOptions(uniqueTechnologies.length ? uniqueTechnologies : fallbackTechnologies);
    } catch (error) {
      console.error('Error fetching enterprise technologies:', error);
      setTechnologyOptions(fallbackTechnologies);
    } finally {
      setTechnologiesLoading(false);
    }
  }, [fallbackTechnologies, organizationId]);

  const handleFunctionSelect = useCallback(
    (value: string) => {
      if (isReadOnlyView || selectedFunction === value) {
        return;
      }

      updateFieldValue('selectedFunction', value);
      const selected = customFunctions.find((func: CustomFunctionOption) => func.name === value);
      updateFieldValue('selectedFunctionId', selected?.id ?? '');
      updateFieldValue('selectedProcessGroup', '');
      updateFieldValue('selectedProcessGroupId', '');
      updateFieldValue('selectedProcess', '');
      updateFieldValue('selectedProcessId', '');
      dispatch(resetProcessGroups());
    },
    [customFunctions, dispatch, selectedFunction, updateFieldValue, isReadOnlyView]
  );

  const handleProcessGroupSelect = useCallback(
    (value: string) => {
      if (isReadOnlyView || selectedProcessGroup === value) {
        return;
      }

      updateFieldValue('selectedProcessGroup', value);
      const selectedGroup = processGroups.find((group: ProcessGroupOption) => group.name === value);
      updateFieldValue('selectedProcessGroupId', selectedGroup?.id ?? '');
      updateFieldValue('selectedProcess', '');
      updateFieldValue('selectedProcessId', '');
      dispatch(resetProcesses());
    },
    [dispatch, processGroups, selectedProcessGroup, updateFieldValue, isReadOnlyView]
  );

  const handleProcessSelect = useCallback(
    (value: string) => {
      if (value === '__create_new_process' || value === '__no_processes') {
        // ignore sentinel values
        return;
      }

      if (isReadOnlyView || selectedProcess === value) {
        return;
      }

      updateFieldValue('selectedProcess', value);
      const matchedProcess = processes.find((proc: ProcessOption) => proc.name === value);
      updateFieldValue('selectedProcessId', matchedProcess?.process_id ?? '');
    },
    [processes, selectedProcess, updateFieldValue, isReadOnlyView]
  );

  const handleTechnologiesChange = useCallback(
    (_event: React.SyntheticEvent<Element, Event>, newValue: string[]) => {
      if (isReadOnlyView) {
        return;
      }
      const sanitizedValues = Array.from(
        new Set(newValue.map((item) => item.trim()).filter(Boolean))
      );
      updateFieldValue('technologies', sanitizedValues);
    },
    [updateFieldValue, isReadOnlyView]
  );

  const predefinedTechnologies = useMemo(() => {
    return technologyOptions.length > 0 ? technologyOptions : fallbackTechnologies;
  }, [fallbackTechnologies, technologyOptions]);

  const allHierarchySelected =
    Boolean(formState.selectedFunctionId) &&
    Boolean(formState.selectedProcessGroupId) &&
    Boolean(formState.selectedProcessId);

  const formSnapshot = useMemo(
    () =>
      JSON.stringify({
        domain,
        selectedFunction,
        selectedFunctionId,
        selectedProcessGroup,
        selectedProcessGroupId,
        selectedProcess,
        selectedProcessId,
        currentProcess,
        expectedImpact,
        specificIdeasOfImprovement,
        keyPerformanceIndicators,
        technologies,
      }),
    [
      currentProcess,
      domain,
      expectedImpact,
      keyPerformanceIndicators,
      selectedFunction,
      selectedFunctionId,
      selectedProcess,
      selectedProcessGroup,
      selectedProcessGroupId,
      selectedProcessId,
      specificIdeasOfImprovement,
      technologies,
    ]
  );

  const hasInitialFormContent = useMemo(() => {
    const hasTextContent =
      Boolean(sanitizeRichTextValue(currentProcess)) ||
      Boolean(sanitizeRichTextValue(expectedImpact)) ||
      Boolean(sanitizeRichTextValue(specificIdeasOfImprovement)) ||
      Boolean(sanitizeRichTextValue(keyPerformanceIndicators));

    const hasHierarchySelection =
      Boolean(domain) ||
      Boolean(selectedFunction) ||
      Boolean(selectedProcessGroup) ||
      Boolean(selectedProcess);

    const hasTechnologies = technologies.length > 0;

    return hasTextContent || hasHierarchySelection || hasTechnologies;
  }, [
    currentProcess,
    domain,
    expectedImpact,
    keyPerformanceIndicators,
    sanitizeRichTextValue,
    selectedFunction,
    selectedProcess,
    selectedProcessGroup,
    specificIdeasOfImprovement,
    technologies.length,
  ]);

  useEffect(() => {
    fetchEnterpriseTechnologies();
  }, [fetchEnterpriseTechnologies]);

  useEffect(() => {
    const topFieldsFilled =
      !!sanitizeRichTextValue(formState.currentProcess) &&
      !!sanitizeRichTextValue(formState.expectedImpact) &&
      allHierarchySelected &&
      formState.technologies &&
      formState.technologies.length > 0;

    onFormCompletionChange?.(topFieldsFilled);
  }, [
    formState.currentProcess,
    formState.expectedImpact,
    formState.technologies,
    allHierarchySelected,
    onFormCompletionChange,
    sanitizeRichTextValue,
  ]);

  useEffect(() => {
    const snapshot = formSnapshot;
    const now = Date.now();

    // For a new use case, trigger the initial POST once, as soon as there is any content
    if (!useCaseId) {
      if (!hasInitialFormContent) {
        lastSubmittedSnapshotRef.current = null;
        return;
      }

      if (isCreatingUseCaseRef.current) {
        return;
      }

      isCreatingUseCaseRef.current = true;

      const initialPayloadRaw: Record<string, unknown> = {
        domain,
        function: selectedFunction,
        function_id: selectedFunctionId,
        process_group: selectedProcessGroup,
        process_group_id: selectedProcessGroupId,
        process: selectedProcess,
        process_id: selectedProcessId,
        current_process: currentProcess,
        expected_impact: expectedImpact,
        organization_id: organizationId,
        created_at: now,
        updated_at: now,
      };

      const initialPayload = removeEmptyFields(
        initialPayloadRaw
      ) as unknown as CreateUseCasePayload;

      void (async () => {
        try {
          const response: CreateUseCaseResponse = await createUseCase(initialPayload);

          if (response) {
            dispatch(updateIdeationFieldState({ field: 'id', value: response.id }));
            dispatch(updateIdeationFieldState({ field: 'status', value: response.status }));
            ShowToast({
              message: 'Your ideation draft has been saved',
              type: ToastTypeEnum.Success,
            });
          }

          lastSubmittedSnapshotRef.current = snapshot;
        } catch (error) {
          console.error('Error creating initial use case:', error);
        } finally {
          isCreatingUseCaseRef.current = false;
        }
      })();

      return;
    }

    if (lastSubmittedSnapshotRef.current === snapshot) {
      return;
    }

    if (patchDebounceTimeoutRef.current) {
      window.clearTimeout(patchDebounceTimeoutRef.current);
    }

    const schedulePatch = () => {
      if (isPatchingUseCaseRef.current) {
        return;
      }

      lastSubmittedSnapshotRef.current = snapshot;
      isPatchingUseCaseRef.current = true;

      const patchPayloadRaw: Record<string, unknown> = {
        domain,
        function: selectedFunction,
        function_id: selectedFunctionId,
        process_group: selectedProcessGroup,
        process_group_id: selectedProcessGroupId,
        process: selectedProcess,
        process_id: selectedProcessId,
        current_process: currentProcess,
        expected_impact: expectedImpact,
        specific_ideas: specificIdeasOfImprovement,
        kpis: keyPerformanceIndicators,
        technologies,
        //linked_sessions: linkedSessions,
        updated_at: Date.now(),
        recent_imported_chat: recent_imported_chat,
      };

      const patchPayload = removeEmptyFields(patchPayloadRaw, {
        keepEmptyStrings: true,
        keepEmptyArrays: true,
      }) as Partial<CreateUseCasePayload> & {
        current_process?: string;
        expected_impact?: string;
        specific_ideas?: string;
        kpis?: string;
        technologies?: string[];
        updated_at: number;
      };

      void (async () => {
        try {
          const response = await updateDraftStatus(useCaseId, patchPayload);
          if (response?.status) {
            dispatch(updateIdeationFieldState({ field: 'status', value: response.status }));
          }
        } catch (error) {
          console.error('Error updating use case:', error);
        } finally {
          isPatchingUseCaseRef.current = false;
        }
      })();
    };

    patchDebounceTimeoutRef.current = window.setTimeout(schedulePatch, 300);
  }, [
    allHierarchySelected,
    currentProcess,
    domain,
    expectedImpact,
    formSnapshot,
    hasInitialFormContent,
    keyPerformanceIndicators,
    organizationId,
    selectedFunction,
    selectedFunctionId,
    selectedProcess,
    selectedProcessGroup,
    selectedProcessGroupId,
    selectedProcessId,
    specificIdeasOfImprovement,
    technologies,
    useCaseId,
    dispatch,
    recent_imported_chat,
  ]);

  useEffect(() => {
    return () => {
      if (patchDebounceTimeoutRef.current) {
        window.clearTimeout(patchDebounceTimeoutRef.current);
        patchDebounceTimeoutRef.current = null;
      }
    };
  }, []);

  // const handleUseDataXplrRequest = useCallback(
  //   (field: OpportunityDetailFieldKey) => {
  //     if (!allHierarchySelected || isReadOnlyView) {
  //       return;
  //     }

  //     const fieldValue = formState[field];
  //     const hasContent = sanitizeRichTextValue(fieldValue as string);

  //     setField(field);

  //     if (hasContent) {
  //       sessionStorage.setItem('sendContextForField', field);
  //       sessionStorage.setItem('contextFieldValue', fieldValue as string);
  //     } else {
  //       // Directly proceed if no content
  //       handleConfirmUseDataXplr(field, false);
  //       sessionStorage.removeItem('sendContextForField');
  //       sessionStorage.removeItem('contextFieldValue');
  //     }

  //     if (
  //       (field === 'currentProcess' && recent_imported_chat?.current_process_chat?.chat_id) ||
  //       (field === 'expectedImpact' && recent_imported_chat?.expected_impact_chat?.chat_id) ||
  //       (field === 'keyPerformanceIndicators' &&
  //         recent_imported_chat?.specific_ideas_of_improvement_chat?.chat_id) ||
  //       (field === 'specificIdeasOfImprovement' &&
  //         recent_imported_chat?.key_performance_indicators_chat?.chat_id)
  //     ) {
  //       setActiveStep('chatbot');
  //     } else {
  //       setActiveStep('process');
  //     }
  //   },
  //   [
  //     allHierarchySelected,
  //     isReadOnlyView,
  //     formState,
  //     sanitizeRichTextValue,
  //     setField,
  //     recent_imported_chat,
  //     setActiveStep,
  //   ]
  // );

  useEffect(() => {
    if (formState.domain && (customFunctions.length === 0 || loadedDomain !== formState.domain)) {
      void loadFunctions();
    }
  }, [formState.domain, customFunctions.length, loadedDomain, loadFunctions]);

  useEffect(() => {
    if (
      formState.selectedFunctionId &&
      (processGroups.length === 0 || loadedFunctionId !== formState.selectedFunctionId)
    ) {
      void loadProcessGroups();
    }
  }, [formState.selectedFunctionId, processGroups.length, loadedFunctionId, loadProcessGroups]);

  useEffect(() => {
    if (
      formState.selectedProcessGroupId &&
      (processes.length === 0 || loadedProcessGroupId !== formState.selectedProcessGroupId)
    ) {
      void loadProcesses();
    }
  }, [formState.selectedProcessGroupId, processes.length, loadedProcessGroupId, loadProcesses]);

  useEffect(() => {
    if (!selectedFunction && selectedFunctionId && customFunctions.length > 0) {
      const matchedFunction = customFunctions.find((func) => func.id === selectedFunctionId);
      if (matchedFunction) {
        updateFieldValue('selectedFunction', matchedFunction.name);
      }
    }
  }, [selectedFunction, selectedFunctionId, customFunctions, updateFieldValue]);

  useEffect(() => {
    if (selectedFunction && !selectedFunctionId && customFunctions.length > 0) {
      const matchedFunction = customFunctions.find((func) => func.name === selectedFunction);
      if (matchedFunction) {
        updateFieldValue('selectedFunctionId', matchedFunction.id);
      }
    }
  }, [selectedFunction, selectedFunctionId, customFunctions, updateFieldValue]);

  useEffect(() => {
    if (!selectedProcessGroup && selectedProcessGroupId && processGroups.length > 0) {
      const matchedGroup = processGroups.find((group) => group.id === selectedProcessGroupId);
      if (matchedGroup) {
        updateFieldValue('selectedProcessGroup', matchedGroup.name);
      }
    }
  }, [selectedProcessGroup, selectedProcessGroupId, processGroups, updateFieldValue]);

  useEffect(() => {
    if (selectedProcessGroup && !selectedProcessGroupId && processGroups.length > 0) {
      const matchedGroup = processGroups.find((group) => group.name === selectedProcessGroup);
      if (matchedGroup) {
        updateFieldValue('selectedProcessGroupId', matchedGroup.id);
      }
    }
  }, [selectedProcessGroup, selectedProcessGroupId, processGroups, updateFieldValue]);

  useEffect(() => {
    if (!selectedProcess && selectedProcessId && processes.length > 0) {
      const matchedProcess = processes.find((proc) => proc.process_id === selectedProcessId);
      if (matchedProcess) {
        updateFieldValue('selectedProcess', matchedProcess.name);
      }
    }
  }, [selectedProcess, selectedProcessId, processes, updateFieldValue]);

  useEffect(() => {
    if (selectedProcess && !selectedProcessId && processes.length > 0) {
      const matchedProcess = processes.find((proc) => proc.name === selectedProcess);
      if (matchedProcess) {
        updateFieldValue('selectedProcessId', matchedProcess.process_id);
      }
    }
  }, [selectedProcess, selectedProcessId, processes, updateFieldValue]);
  // const handleConfirmUseDataXplr = useCallback(
  //   (field: keyof NewIdeationState, sendContext: boolean) => {
  //     const selectedField: keyof NewIdeationState = field;
  //     if (!selectedField) {
  //       return;
  //     }

  //     setField(selectedField);

  //     // Store whether to send context and the field value
  //     if (sendContext) {
  //       sessionStorage.setItem('sendContextForField', selectedField);
  //       const fieldValue = formState[selectedField];
  //       if (fieldValue) {
  //         sessionStorage.setItem('contextFieldValue', fieldValue as string);
  //       }
  //     } else {
  //       sessionStorage.removeItem('sendContextForField');
  //       sessionStorage.removeItem('contextFieldValue');
  //     }

  //     if (
  //       (selectedField === 'currentProcess' &&
  //         recent_imported_chat?.current_process_chat?.chat_id) ||
  //       (selectedField === 'expectedImpact' &&
  //         recent_imported_chat?.expected_impact_chat?.chat_id) ||
  //       (selectedField === 'keyPerformanceIndicators' &&
  //         recent_imported_chat?.specific_ideas_of_improvement_chat?.chat_id) ||
  //       (selectedField === 'specificIdeasOfImprovement' &&
  //         recent_imported_chat?.key_performance_indicators_chat?.chat_id)
  //     ) {
  //       setActiveStep('chatbot');
  //     } else {
  //       setActiveStep('process');
  //     }
  //   },
  //   [formState, recent_imported_chat, setActiveStep, setField]
  // );

  const handleCreateNewProcess = useCallback(async () => {
    try {
      if (newProcessName.trim() !== '' && selectedProcessGroupId) {
        const response = await createNewProcess(newProcessName, selectedProcessGroupId);
        if (response) {
          console.log('New process created:', response);
          const addProcessResponse = await addProcessInOrganization([response.id]);
          console.log('Process added to organization:', addProcessResponse);
          if (addProcessResponse) {
            setCreateNewProcessModalOpen(false);
            setNewProcessName('');
            updateFieldValue('selectedProcess', response.name);
            updateFieldValue('selectedProcessId', response.id);
            dispatch(resetProcesses());
            await loadProcesses();
          }
        }
      }
    } catch (error) {
      console.error('Error creating new process:', error);
    }
  }, [newProcessName, selectedProcessGroupId, updateFieldValue, dispatch, loadProcesses]);

  const onAdd = useCallback(
    async (selectedIds: string[]) => {
      const fieldLabelToFieldKey: Record<string, OpportunityDetailFieldKey> = {
        'Current Process': 'currentProcess',
        'Expected Impact': 'expectedImpact',
        'Specific Idea(s) for Improvement': 'specificIdeasOfImprovement',
        'Key Performance Information': 'keyPerformanceIndicators',
        KPIs: 'keyPerformanceIndicators',
        'Specific Idea of Improvement': 'specificIdeasOfImprovement',
      };

      const fieldKey = fieldLabelToFieldKey[currentFieldLabel];

      if (!fieldKey || selectedIds.length === 0) return;

      // Get the selected sessions for linking
      const selectedSessions = sessions.filter((s) => selectedIds.includes(s.id));
      const linkedSessions = selectedSessions.map((s) => ({
        id: s.id,
        use_case_title: s.use_case_title,
        session_name: s.session_name,
        created_by: s.created_by,
        created_at: s.created_at,
        conversation_id: s.conversation_id,
        field_type: s.usedInProcess,
        semantic_knowledge: s.semantic_knowledge,
        ideation_intake: s.ideation_intake,
      }));

      // Store the linked sessions in Redux
      dispatch(addLinkedSessions({ field: fieldKey, sessions: linkedSessions }));

      const htmlReports: string[] = [];
      for (const session of selectedSessions) {
        let reportContent = '';
        if (session?.semantic_knowledge) {
          if (typeof session.semantic_knowledge === 'object' && session.semantic_knowledge.report) {
            reportContent = session.semantic_knowledge.report;
          } else if (typeof session.semantic_knowledge === 'string') {
            reportContent = session.semantic_knowledge;
          }
        }

        if (reportContent) {
          const htmlReport = await marked.parse(reportContent);
          htmlReports.push(htmlReport);
        }
      }

      if (htmlReports.length > 0) {
        const currentValue = formState[fieldKey] as string;
        const newValue = currentValue
          ? currentValue + '<br><br>' + htmlReports.join('<br><br>')
          : htmlReports.join('<br><br>');
        handleChange(fieldKey, newValue, true); // Preserve linked sessions when adding semantic knowledge
      }

      setDrawerOpen(false);
    },
    [currentFieldLabel, sessions, formState, handleChange, dispatch]
  );

  const handleUseDataXplr = useCallback((label: string) => {
    setSelectedCategory(label);
    setCurrentFieldLabel(label);
    setDrawerOpen(true);
  }, []);

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mb: 2,
          }}
        >
          <Typography
            variant="body1"
            fontWeight={700}
            sx={{ fontSize: '22px', fontFamily: 'Poppins', color: '#313131' }}
          >
            Discover new AI Opportunities
          </Typography>
          <Box
            sx={{
              height: 'calc(100vh - 330px)',
              overflowY: 'auto',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                width: '8px',
                marginBottom: '10px',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                marginTop: '100px',
                marginBottom: '100px',
                width: '8px',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555',
              },
              pr: { xs: 0, md: 2, lg: 4 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {/* Domain */}
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ fontSize: '16px', fontFamily: 'Poppins', color: '#131426' }}
                  >
                    Domain
                  </Typography>
                  <Typography sx={{ color: 'red' }}>*</Typography>
                </Box>
                <Select
                  value={formState.domain}
                  onChange={(event) => handleDomainChange(event.target.value as string)}
                  fullWidth
                  size="small"
                  displayEmpty
                  disabled={isReadOnlyView}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select domain
                  </MenuItem>
                  {DOMAIN_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Function */}
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body1" fontWeight={600} sx={{ fontSize: '16px' }}>
                    Function
                  </Typography>
                  <Typography sx={{ color: 'red' }}>*</Typography>
                </Box>
                <Select
                  value={formState.selectedFunction}
                  onChange={(event) => handleFunctionSelect(event.target.value as string)}
                  onOpen={loadFunctions}
                  fullWidth
                  size="small"
                  displayEmpty
                  disabled={
                    isReadOnlyView || !formState.domain || !(formState.domain in DOMAIN_OFFICE_MAP)
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    {functionsLoading ? 'Loading functions...' : 'Select function'}
                  </MenuItem>
                  {customFunctions.map((func: CustomFunctionOption) => (
                    <MenuItem key={func.id} value={func.name}>
                      {func.name}
                    </MenuItem>
                  ))}
                  {!functionsLoading && customFunctions.length === 0 && (
                    <MenuItem value="__no_functions" disabled>
                      No functions available
                    </MenuItem>
                  )}
                </Select>
              </Box>

              {/* Process Group */}
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body1" fontWeight={600} sx={{ fontSize: '16px' }}>
                    Process Group
                  </Typography>
                  <Typography sx={{ color: 'red' }}>*</Typography>
                </Box>
                <Select
                  value={formState.selectedProcessGroup}
                  onChange={(event) => handleProcessGroupSelect(event.target.value as string)}
                  onOpen={loadProcessGroups}
                  fullWidth
                  size="small"
                  displayEmpty
                  disabled={isReadOnlyView || !formState.selectedFunctionId}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    {processGroupsLoading ? 'Loading process groups...' : 'Select process group'}
                  </MenuItem>
                  {processGroups.map((group: ProcessGroupOption) => (
                    <MenuItem key={group.id} value={group.name}>
                      {group.name}
                    </MenuItem>
                  ))}
                  {!processGroupsLoading && processGroups.length === 0 && (
                    <MenuItem value="__no_process_groups" disabled>
                      No process groups available
                    </MenuItem>
                  )}
                </Select>
              </Box>

              {/* Process */}
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body1" fontWeight={600} sx={{ fontSize: '16px' }}>
                    Process
                  </Typography>
                  <Typography sx={{ color: 'red' }}>*</Typography>
                </Box>
                <Select
                  value={formState.selectedProcess}
                  onChange={(event) => {
                    const value = event.target.value as string;
                    handleProcessSelect(value);
                    if (value !== '__create_new_process') {
                      setProcessSelectOpen(false);
                    }
                  }}
                  onOpen={() => {
                    setProcessSelectOpen(true);
                    loadProcesses();
                  }}
                  onClose={() => {
                    setProcessSelectOpen(false);
                  }}
                  open={processSelectOpen}
                  fullWidth
                  size="small"
                  displayEmpty
                  disabled={isReadOnlyView || !formState.selectedProcessGroupId}
                  renderValue={(selected) => {
                    if (!selected) {
                      return processesLoading ? 'Loading processes...' : 'Select process';
                    }
                    return selected;
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                      },
                    },
                    MenuListProps: {
                      sx: {
                        maxHeight: '240px',
                        overflowY: 'auto',
                        p: 0,
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#f1f1f1',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#888',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: '#555',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    {processesLoading ? 'Loading processes...' : 'Select process'}
                  </MenuItem>
                  {processes.map((proc: ProcessOption) => (
                    <MenuItem key={proc.process_id} value={proc.name}>
                      {proc.name}
                    </MenuItem>
                  ))}
                  {!processesLoading && processes.length === 0 && (
                    <MenuItem value="__no_processes" disabled>
                      No processes available
                    </MenuItem>
                  )}
                  <MenuItem
                    value="__create_new_process"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCreateNewProcessModalOpen(true);
                      setProcessSelectOpen(false);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      color: '#1A55F9',
                      p: 2,
                      borderTop: '1px solid #E0E0E0',
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    <AddCircleOutlineIcon />
                    <Typography variant="body2" sx={{ fontSize: '16px' }}>
                      Create New
                    </Typography>
                  </MenuItem>
                </Select>
              </Box>
            </Box>
            <OpportunityMultilineField
              label="Current Process"
              placeholder="Describe your current as-is-process"
              value={formState.currentProcess}
              required
              onChange={(value) => handleChange('currentProcess', value)}
              disabled={isReadOnlyView}
              onUseDataXplrClick={
                isReadOnlyView ? undefined : () => handleUseDataXplr('Current Process')
              }
              linkedSessions={linkedSessions.currentProcess || []}
              fieldCategory="current_process"
            />
            <OpportunityMultilineField
              label="Expected Impact"
              placeholder="Describe your expected impact with annualized dollar benefits. Please be specific and quantify the expected impact"
              value={formState.expectedImpact}
              required
              onChange={(value) => handleChange('expectedImpact', value)}
              disabled={isReadOnlyView}
              onUseDataXplrClick={
                isReadOnlyView ? undefined : () => handleUseDataXplr('Expected Impact')
              }
              linkedSessions={linkedSessions.expectedImpact || []}
              fieldCategory="expected_impact"
            />
            <OpportunityMultilineField
              label="Specific Idea(s) for Improvement"
              placeholder="Share detailed idea(s) for improvement related to the as-is work steps outlined above."
              value={formState.specificIdeasOfImprovement}
              onChange={(value) => handleChange('specificIdeasOfImprovement', value)}
              disabled={isReadOnlyView}
              onUseDataXplrClick={
                isReadOnlyView ? undefined : () => handleUseDataXplr('Specific Idea of Improvement')
              }
              linkedSessions={linkedSessions.specificIdeasOfImprovement || []}
              fieldCategory="ideas_of_improvement"
            />
            <OpportunityMultilineField
              label="Key Performance Indicators"
              placeholder="Describe your KPIs linked with the as-is process explained above."
              value={formState.keyPerformanceIndicators}
              onChange={(value) => handleChange('keyPerformanceIndicators', value)}
              disabled={isReadOnlyView}
              onUseDataXplrClick={isReadOnlyView ? undefined : () => handleUseDataXplr('KPIs')}
              linkedSessions={linkedSessions.keyPerformanceIndicators || []}
              fieldCategory="key_performance_indicator"
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body1" fontWeight={600} sx={{ fontSize: '16px' }}>
                  Enterprise Applications in Use
                </Typography>
                <Typography sx={{ color: 'red' }}>*</Typography>
              </Box>
              <Autocomplete
                multiple
                freeSolo
                options={predefinedTechnologies}
                value={formState.technologies}
                onChange={handleTechnologiesChange}
                loading={technologiesLoading}
                loadingText="Loading enterprise applications..."
                noOptionsText={
                  technologiesLoading ? 'Loading...' : 'No enterprise applications found'
                }
                disabled={isReadOnlyView}
                renderTags={(value: string[], getTagProps) =>
                  value.map((option: string, index: number) => {
                    const tagProps = getTagProps({ index });
                    const { key, ...rest } = tagProps;

                    return (
                      <Chip
                        key={key}
                        label={option}
                        {...rest}
                        sx={{
                          backgroundColor: '#f0f4ff',
                          borderRadius: '6px',
                          border: '1px solid',
                          borderColor: '#1A55F9',
                          color: 'primary.main',
                          fontWeight: 400,
                          fontSize: 14,
                          height: 28,
                          '& .MuiChip-deleteIcon': {
                            marginRight: '4px',
                            color: 'primary.main',
                            fontSize: 16,
                          },
                          '& .MuiChip-deleteIcon:hover': {
                            color: 'error.main',
                            backgroundColor: 'transparent',
                          },
                        }}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Mention the core systems currently used in the process explained above"
                    sx={{
                      '& .MuiInputBase-input::placeholder': {
                        color: '#8d9499',
                        opacity: 1,
                      },
                      marginBottom: 10,
                      fontSize: 16,
                      backgroundColor:
                        allHierarchySelected && !isReadOnlyView ? 'white' : '#f5f5f5',
                      cursor: allHierarchySelected && !isReadOnlyView ? 'text' : 'not-allowed',
                      '& .MuiOutlinedInput-root': {
                        cursor: allHierarchySelected && !isReadOnlyView ? 'text' : 'not-allowed',
                        '&:hover fieldset': {
                          borderRadius: '6px',
                          borderColor: '#1A55F9',
                          borderWidth: 1,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e0e0e0',
                          borderRadius: '6px',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1A55F9',
                          borderWidth: 1,
                        },
                      },
                    }}
                  />
                )}
                sx={{
                  backgroundColor: allHierarchySelected && !isReadOnlyView ? 'white' : 'white',
                  cursor: allHierarchySelected && !isReadOnlyView ? 'text' : 'not-allowed',
                  '& .MuiOutlinedInput-root': {
                    paddingY: '4px',
                    backgroundColor: allHierarchySelected && !isReadOnlyView ? 'white' : '#f5f5f5',
                    cursor: allHierarchySelected && !isReadOnlyView ? 'text' : 'not-allowed',
                  },
                  '& .MuiInputBase-root': {
                    cursor: allHierarchySelected && !isReadOnlyView ? 'text' : 'not-allowed',
                  },
                  '& .MuiChip-root': {
                    cursor: allHierarchySelected && !isReadOnlyView ? 'default' : 'not-allowed',
                  },
                  '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
                    cursor: allHierarchySelected && !isReadOnlyView ? 'pointer' : 'not-allowed',
                    pointerEvents: allHierarchySelected && !isReadOnlyView ? 'auto' : 'none',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        <RightChatDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sessions={sessions}
          onAdd={onAdd}
          onCategoryChange={handleCategoryChange}
          onSearchChange={handleSearchChange}
          onRefetch={() => loadSessions(getFieldTypeFromCategory(selectedCategory))}
          useCaseId={useCaseId}
          currentFieldLabel={currentFieldLabel}
        />

        <Modal
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          open={createNewProcessModalOpen}
          onClose={() => setCreateNewProcessModalOpen(false)}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '60rem',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #E0E0E0',
                p: 2,
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: '16px' }}>
                Create New
              </Typography>
              <CloseIcon
                sx={{ color: '#000', cursor: 'pointer' }}
                width="12px"
                onClick={() => setCreateNewProcessModalOpen(false)}
              />
            </Box>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="body1"
                fontWeight={400}
                sx={{ fontSize: '14px', color: '#131426' }}
              >
                Process Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Enter Process Name"
                value={newProcessName}
                onChange={(e) => setNewProcessName(e.target.value)}
              />
            </Box>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setCreateNewProcessModalOpen(false)}
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={handleCreateNewProcess}>
                Create
              </Button>
            </Box>
          </Box>
        </Modal>
      </Box>
      {evaluateObject?.isOpen && (
        <Grid
          sx={{
            md: 3,
            xs: 12,
            display: 'block',
            pl: '16px',
            pr: '0px',
            paddingTop: '32px',
          }}
        >
          <EvaluationReportPanel
            markdownContent={`${evaluateObject?.data || ''}`}
            onClose={() => {
              if (handleCloseEvaluate) {
                handleCloseEvaluate();
              }
            }}
            loading={evaluateObject?.loading}
            isAiSolutionBlueprint={true}
            isMinimized={true}
            isFitToContent={true}
          />
        </Grid>
      )}
    </Box>
  );
};

export default OpportunityDetails;
